<template>
  <div class="game-containerbackground bg-dark">
    <h3 style="color: #fff">{{ langTexts.title }}</h3>

    <div class="background">
      <!-- Language switcher -->
      <div class="language-switcher">
        <label>{{ langTexts.switchLanguage }}: </label>
        <select v-model="currentLang">
          <option value="cn">中文简体</option>
          <option value="tw">中文繁體</option>
          <option value="en">English</option>
          <option value="my">Malay</option>
        </select>
      </div>

      <!-- Custom input round -->
      <div class="custom-input">
        <label>{{ langTexts.customInputLabel }}:</label>
        <input
          v-model="userInput"
          :placeholder="langTexts.customInputPlaceholder"
        />
        <button class="button" @click="startCustomRound">
          {{ langTexts.startCustomRound }}
        </button>
      </div>

      <!-- Score bar -->
      <div class="score-bar">
        <div class="score correct">✅ {{ correctScore }}</div>
        <div class="score wrong">❌ {{ wrongScore }}</div>
      </div>
      <!-- Center character -->
      <div class="center-character">
        <span>{{ currentCharacter }}</span>
      </div>

      <!-- Bubbles -->
      <div class="bubbles">
        <div
          v-for="(bubble, index) in bubbles"
          :key="index"
          class="bubble"
          :class="{
            correct: bubble.correctClicked,
            wrong: bubble.wrongClicked,
          }"
          :style="{ top: bubble.top + '%', left: bubble.left + '%' }"
          @click="handleClick(bubble)"
        >
          {{ bubble.char }}
        </div>
      </div>

      <!-- Next round button -->
      <button @click="nextRound">{{ langTexts.nextRound }}</button>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, computed, watch, onMounted } from "vue";

// --- Language text mapping ---
const languages = {
  cn: {
    title: "幼儿识字启蒙",
    switchLanguage: "切换语言",
    nextRound: "下一轮",
    customInputLabel: "自定义字/词",
    customInputPlaceholder: "输入 2-20 个字，以逗号或空格分隔",
    startCustomRound: "开始自定义轮",
    customInputAlert: "请输入至少 2 个字！",
  },
  tw: {
    title: "幼兒識字啟蒙",
    switchLanguage: "切換語言",
    nextRound: "下一輪",
    customInputLabel: "自訂字/詞",
    customInputPlaceholder: "輸入 2-20 個字，以逗號或空格分隔",
    startCustomRound: "開始自訂輪",
    customInputAlert: "請輸入至少 2 個字！",
  },
  en: {
    title: "Early Literacy for Kids",
    switchLanguage: "Switch Language",
    nextRound: "Next Round",
    customInputLabel: "Custom Characters/Words",
    customInputPlaceholder:
      "Enter 2-20 letters/words separated by comma or space",
    startCustomRound: "Start Custom Round",
    customInputAlert: "Please enter at least 2 characters/words!",
  },
  my: {
    title: "Pengenalan Huruf Kanak-Kanak",
    switchLanguage: "Tukar Bahasa",
    nextRound: "Pusingan Baru",
    customInputLabel: "Huruf/Kata Tersuai",
    customInputPlaceholder:
      "Masukkan 2-20 huruf/kata, pisahkan dengan koma atau ruang",
    startCustomRound: "Mula Pusingan Tersuai",
    customInputAlert: "Sila masukkan sekurang-kurangnya 2 huruf/kata!",
  },
};

const currentLang = ref("cn");
const langTexts = computed(() => languages[currentLang.value]);

// --- Kid-friendly characters per language ---
const characterPools = {
  cn: [
    "大",
    "小",
    "天",
    "口",
    "山",
    "水",
    "日",
    "月",
    "火",
    "木",
    "土",
    "人",
    "马",
    "鸟",
    "鱼",
    "花",
    "狗",
    "猫",
    "车",
  ],
  tw: [
    "大",
    "小",
    "天",
    "口",
    "山",
    "水",
    "日",
    "月",
    "火",
    "木",
    "土",
    "人",
    "馬",
    "鳥",
    "魚",
    "花",
    "狗",
    "貓",
    "車",
  ],
  en: [
    "A",
    "B",
    "C",
    "D",
    "E",
    "F",
    "G",
    "H",
    "I",
    "J",
    "K",
    "L",
    "M",
    "N",
    "O",
    "P",
    "Q",
    "R",
    "S",
    "T",
  ],
  my: [
    "a",
    "b",
    "c",
    "d",
    "e",
    "f",
    "g",
    "h",
    "i",
    "j",
    "k",
    "l",
    "m",
    "n",
    "o",
    "p",
    "q",
    "r",
    "s",
    "t",
  ],
};

const correctScore = ref(0);
const wrongScore = ref(0);
const currentCharacter = ref("");
const bubbles = reactive([]);
const userInput = ref(""); // user input string

const minBubbles = 2;
const maxBubbles = 10;

function randomInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

// --- Default / next round ---
function nextRound() {
  const pool = characterPools[currentLang.value];
  currentCharacter.value = pool[randomInt(0, pool.length - 1)];

  generateBubbles(pool);
}

// --- Generate bubbles from a pool ---
function generateBubbles(pool) {
  const bubbleCount = randomInt(minBubbles, maxBubbles);
  bubbles.splice(0, bubbles.length);

  const positions = [];

  for (let i = 0; i < bubbleCount; i++) {
    let char =
      Math.random() < 0.6
        ? currentCharacter.value
        : pool[randomInt(0, pool.length - 1)];

    let top,
      left,
      attempts = 0;
    const size = 60; // bubble size in px, for spacing calculation
    const padding = 5; // minimum spacing in %

    do {
      top = randomInt(5, 85);
      left = randomInt(5, 85);
      attempts++;
    } while (
      positions.some(
        (pos) =>
          Math.abs(pos.top - top) < size / 3 &&
          Math.abs(pos.left - left) < size / 3
      ) &&
      attempts < 100
    );

    positions.push({ top, left });

    bubbles.push({
      char,
      top,
      left,
      correctClicked: false,
      wrongClicked: false,
    });
  }
}

// --- Handle bubble click ---
function handleClick(bubble) {
  if (bubble.correctClicked || bubble.wrongClicked) return;

  if (bubble.char === currentCharacter.value) {
    bubble.correctClicked = true;
    correctScore.value += 1;
  } else {
    bubble.wrongClicked = true;
    wrongScore.value += 1;
  }
}

// --- Animate bubbles ---
function animateBubbles() {
  bubbles.forEach((bubble) => {
    bubble.top += Math.sin(Date.now() / 500 + bubble.left) * 0.2;
    bubble.left += Math.cos(Date.now() / 500 + bubble.top) * 0.2;
    bubble.top = Math.min(Math.max(bubble.top, 5), 85);
    bubble.left = Math.min(Math.max(bubble.left, 5), 85);
  });
  requestAnimationFrame(animateBubbles);
}

// --- Start custom input round ---
function startCustomRound() {
  const rawChars = userInput.value
    .split(/[\s,]+/)
    .filter((c) => c.trim() !== "");
  if (rawChars.length < 2) {
    alert(langTexts.value.customInputAlert);
    return;
  }

  currentCharacter.value = rawChars[randomInt(0, rawChars.length - 1)];
  generateBubbles(rawChars);
}

// --- Watch language change ---
watch(currentLang, () => {
  nextRound();
});

// --- On mounted ---
onMounted(() => {
  nextRound();
  animateBubbles();
});
</script>

<style scoped>
.game-container {
  text-align: center;
  font-family: "Comic Sans MS", sans-serif;
  padding: 20px;
  background-color: #1e1e1e; /* dark background */
  min-height: 100vh;
  color: #fff;
}

.score-bar {
  display: flex;
  justify-content: center; /* center both scores */
  gap: 50px; /* space between correct & wrong */
  margin: 20px 0;
  font-size: 24px;
  font-weight: bold;
}

.score.correct {
  color: #2a9d8f;
  text-shadow: 1px 1px 3px #000;
}

.score.wrong {
  color: #e63946;
  text-shadow: 1px 1px 3px #000;
}

.language-switcher,
.custom-input {
  margin-bottom: 20px;
  color: #fff;
}

.language-switcher select,
.custom-input input {
  padding: 6px 10px;
  border-radius: 5px;
  border: none;
  font-size: 16px;
}

.center-character {
  font-size: 100px;
  margin: 25px 0;
  font-weight: bold;
  color: #fff;
  text-shadow: 2px 2px 5px #000;
}

.bubbles {
  position: relative;
  width: 100%;
  height: 300px;
  margin: 0 auto 20px auto;
}

.bubble {
  position: absolute;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: #f9c74f;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 32px;
  cursor: pointer;
  user-select: none;
  transition: all 0.3s;
}

.bubble.correct {
  background-color: #90be6d;
  transform: scale(1.2);
}

.bubble.wrong {
  background-color: #f94144;
  transform: scale(0.9);
}

/* Standardized buttons */
.button,
button {
  padding: 10px 20px;
  font-size: 16px;
  border: 2px solid transparent;
  border-radius: 5px;
  background-color: transparent;
  color: #fff;
  cursor: pointer;
  transition: transform 0.2s, font-size 0.2s, border-color 0.2s;
  z-index: 99;
}

.button:hover,
button:hover {
  transform: scale(1.05);
  border-color: #ffd166;
  color: #ffd166;
}
</style>
